import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useApi } from '../auth/ApiProvider';
import { useI18n } from '../i18n/I18nProvider';
import { Badge, Button, Card, Row, Stack } from './primitives';
import { NotificationPreferencesGrid } from './Account';
import { EmptyState, ErrorState, LoadingState } from './states';

/**
 * The in-app notification centre and its preferences (FR-NOT-06, FR-NOT-09).
 *
 * Everything raised is readable here whether or not a push ever arrived, which is the point: delivery in
 * the target market is unreliable enough that a missed push must never mean lost information.
 *
 * The mandatory types — a child arriving, leaving, an incident, an unauthorised collection, and since Q19 a
 * direct message and an announcement — are listed with their in-app switch locked rather than hidden. A
 * family should be able to see that those are guaranteed, which is also the reason the routine updates are
 * batched into a digest. Their email and SMS
 * copies can still be switched off, on the same grid as every other channel (FR-NOT-06).
 */
export function NotificationCentre({ showPreferences = true }: {
  /** False for the platform console: preferences belong to a tenant membership (decision 2026-09-23 #15). */
  showPreferences?: boolean;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<'inbox' | 'preferences'>('inbox');

  if (!showPreferences) return <Inbox />;

  return (
    <Stack>
      <Row>
        <Button variant={tab === 'inbox' ? 'primary' : 'ghost'} onClick={() => setTab('inbox')}>
          {t('notifications.title')}
        </Button>
        <Button
          variant={tab === 'preferences' ? 'primary' : 'ghost'}
          onClick={() => setTab('preferences')}
        >
          {t('notifications.preferences')}
        </Button>
      </Row>

      {tab === 'inbox' ? <Inbox /> : <NotificationPreferencesGrid />}
    </Stack>
  );
}

function Inbox() {
  const api = useApi();
  const { t, formatDateTime } = useI18n();
  const queryClient = useQueryClient();

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications.list(),
  });

  const markAll = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markOne = useMutation({
    mutationFn: (id: number) => api.notifications.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  if (notifications.isPending) return <LoadingState />;
  if (notifications.isError) {
    return <ErrorState error={notifications.error} onRetry={() => void notifications.refetch()} />;
  }

  if (notifications.data.items.length === 0) {
    return <EmptyState icon="🔔" title={t('notifications.empty')} />;
  }

  const unread = notifications.data.items.filter((item) => item.readAt == null).length;

  return (
    <Stack>
      {unread > 0 && (
        <Row between>
          <Badge tone="count">{unread}</Badge>
          <Button variant="ghost" busy={markAll.isPending} onClick={() => markAll.mutate()}>
            {t('notifications.markAllRead')}
          </Button>
        </Row>
      )}

      {notifications.data.items.map((item) => (
        <Card key={item.id}>
          <Row between>
            <span className="dg-stack dg-stack--tight">
              <strong>{item.subject ?? t(`care.kind.Note`)}</strong>
              {item.body && <span>{item.body}</span>}
              <span className="dg-muted dg-text-sm">{formatDateTime(item.createdAt)}</span>
            </span>
            {item.readAt == null && (
              <Button variant="ghost" onClick={() => markOne.mutate(item.id)}>
                {t('messages.read')}
              </Button>
            )}
          </Row>
        </Card>
      ))}
    </Stack>
  );
}
